/**
 * Document presentation helpers (CR2). Single source of truth so the upload
 * stepper (cr2a) and the documents library (cr2b) render doc types + statuses
 * identically. Pure data/strings only — no JSX (icons are mapped to Lucide
 * components the consumer renders).
 */
import {
  FileText,
  ClipboardList,
  Folder,
  Target,
  Users,
  Building2,
  File,
  type LucideIcon,
} from 'lucide-react';
import { DOC_TYPES, MAX_UPLOAD_BYTES_DEFAULT, type DocType, type DocStatus } from '@klinik/shared';
import type { BadgeTone } from '../components/ui/Badge';

/** The six intake categories shown as upload tiles (cr2a), in display order. */
export const INTAKE_DOC_TYPES: DocType[] = [
  'financial_report',
  'agreement',
  'sop',
  'marketing_program',
  'org_structure',
  'hr_data',
];

interface DocTypeMeta {
  /** Full label for the upload tile. */
  label: string;
  /** Short tag used on library cards. */
  tag: string;
  icon: LucideIcon;
}

const DOC_TYPE_META: Record<DocType, DocTypeMeta> = {
  financial_report: { label: 'Laporan Keuangan 1 Tahun', tag: 'Keuangan', icon: FileText },
  agreement: { label: 'Akad / Perjanjian (internal & vendor)', tag: 'Akad', icon: ClipboardList },
  sop: { label: 'Dokumen SOP', tag: 'SOP', icon: Folder },
  marketing_program: { label: 'Program Pemasaran Berjalan', tag: 'Pemasaran', icon: Target },
  org_structure: { label: 'Struktur Organisasi', tag: 'Organisasi', icon: Users },
  hr_data: { label: 'Jumlah Karyawan & Data SDM', tag: 'SDM', icon: Building2 },
  other: { label: 'Dokumen Lain', tag: 'Lainnya', icon: File },
};

export function docTypeMeta(docType: DocType): DocTypeMeta {
  return DOC_TYPE_META[docType] ?? DOC_TYPE_META.other;
}

/** Human label + badge tone for every DocStatus. Unknown → neutral draft badge. */
interface DocStatusMeta {
  label: string;
  tone: BadgeTone;
}

const DOC_STATUS_META: Record<string, DocStatusMeta> = {
  pending_upload: { label: 'Menunggu unggah', tone: 'draft' },
  uploaded: { label: 'Terunggah', tone: 'done' },
  queued: { label: 'Antri analisa', tone: 'progress' },
  parsing: { label: 'Memproses', tone: 'progress' },
  parsed: { label: 'Terbaca', tone: 'sent' },
  indexed: { label: 'Terindeks AI', tone: 'ai' },
  failed: { label: 'Gagal', tone: 'critical' },
  // Defensive: status the API may add later (decisions.md mentions OCR). Unknown
  // statuses fall through to a neutral badge via docStatusMeta().
  needs_ocr: { label: 'Perlu OCR', tone: 'watch' },
};

export function docStatusMeta(status: DocStatus | string): DocStatusMeta {
  return DOC_STATUS_META[status] ?? { label: status, tone: 'draft' };
}

/** True only once a doc is fully embedded into pgvector (CR3 terminal state). */
export function isFullyIndexed(status: DocStatus | string): boolean {
  return status === 'indexed';
}

/** Format bytes → "1.2 MB" / "640 KB" for tile + card meta. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** Coarse extension label (PDF / XLSX / …) from filename or content type. */
export function fileKindLabel(filename: string, contentType: string | null): string {
  const ext = filename.includes('.') ? filename.split('.').pop()!.toUpperCase() : '';
  if (ext) return ext;
  if (contentType?.includes('pdf')) return 'PDF';
  if (contentType?.includes('sheet') || contentType?.includes('excel')) return 'XLSX';
  return 'FILE';
}

/** Accepted upload formats (decisions.md / CR2 scope): PDF + Excel. */
export const ACCEPT_ATTR = '.pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_BYTES_DEFAULT; // shared cap (25 MB)

const ACCEPTED_EXT = ['pdf', 'xls', 'xlsx'];

/** Client-side guard mirroring the API's validation (which is authoritative). */
export function validateFile(file: File): string | null {
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  if (!ACCEPTED_EXT.includes(ext)) return 'Format tidak didukung. Gunakan PDF atau Excel (.xls/.xlsx).';
  if (file.size > MAX_UPLOAD_BYTES) return 'Ukuran file melebihi 25 MB.';
  if (file.size <= 0) return 'File kosong tidak dapat diunggah.';
  return null;
}

/** Best-effort content type for the initiate payload (R2 stores the bytes as-is). */
export function contentTypeFor(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  return 'application/octet-stream';
}

export { DOC_TYPES };
